import {put, select, take, fork, call} from 'redux-saga/effects';
import {efServiceAssignmentTypes, BusinessContactDetails, Location} from '@ace-de/eua-entity-types';
import fetchRequest from '../../application/sagas/fetchRequest';
import updateServiceAssignment from '../../service-assignments/sagas/updateServiceAssignment';
import * as serviceAssignmentHelpers from '../../service-assignments/sagas/serviceAssignmentHelpers';
import * as saaActionTypes from '../saaActionTypes';
import getMapExtentFromCenter from '../../utils/getMapExtentFromCenter';

/**
 * Search SAA accommodation locations via Google APIs
 */

const searchSAAAccommodationLocationsByGoogle = function* searchSAAAccommodationLocationsByGoogle({payload}) {
    const {serviceCaseId, serviceAssignmentLineNo, referencePosition, mapExtent: currentMapExtent = null} = payload;
    const {serviceAssignments} = yield select(state => state.serviceAssignments);
    const serviceAssignmentId = `${serviceCaseId}-${serviceAssignmentLineNo}`;
    const serviceAssignment = serviceAssignments[serviceAssignmentId];
    if (!serviceAssignment || !referencePosition) return;

    const mapExtent = currentMapExtent ?? getMapExtentFromCenter(
        referencePosition.latitude,
        referencePosition.longitude,
        11,
        window.innerWidth,
        window.innerHeight,
    );

    yield put({
        type: saaActionTypes.SET_SAA_ACCOMMODATION_LOCATION_RECOMMENDATIONS,
        payload: {serviceAssignmentId, accommodationLocationRecommendationDTOs: []},
    });

    const {serviceManager} = yield select(state => state.application);
    const googleMapsService = serviceManager.loadService('googleMapsService');
    const i18nService = serviceManager.loadService('i18nService');
    const {translate} = i18nService;
    const acePartnerLocation = serviceAssignment.acePartner?.location;

    yield* serviceAssignmentHelpers.setPersistencePending(serviceAssignmentId);

    const {longestDistance} = yield call(googleMapsService.getLongestDistanceFromPoint,
        referencePosition,
        mapExtent);
    const mapViewport = {
        southwest: {
            longitude: mapExtent.xmin,
            latitude: mapExtent.ymin,
        },
        northeast: {
            longitude: mapExtent.xmax,
            latitude: mapExtent.ymax,
        },
    };

    yield fork(
        fetchRequest,
        saaActionTypes.SEARCH_ACCOMMODATION_LOCATION_GOOGLE_PLACES_REQUEST,
        googleMapsService.searchPlaces,
        {
            searchQuery: `${referencePosition?.formattedAddress} ${translate('saa_accommodation_tab.input.search_query_string')}`,
            longitude: referencePosition.longitude,
            latitude: referencePosition.latitude,
            locationRadius: Math.floor(longestDistance) * 1000 || null,
            viewport: mapViewport
                ? {
                    southwest: mapViewport.southwest,
                    northeast: mapViewport.northeast,
                }
                : null,
        },
    );

    const placesSearchResponseAction = yield take([
        saaActionTypes.SEARCH_ACCOMMODATION_LOCATION_GOOGLE_PLACES_REQUEST_FAILED,
        saaActionTypes.SEARCH_ACCOMMODATION_LOCATION_GOOGLE_PLACES_REQUEST_SUCCEEDED,
    ]);

    if (placesSearchResponseAction.error) {
        yield* serviceAssignmentHelpers.setPersistenceReady(serviceAssignmentId);
        return;
    }

    const {response: placesSearchResponse} = placesSearchResponseAction.payload;
    const {googlePlaceDTOs} = placesSearchResponse;

    yield fork(
        fetchRequest,
        saaActionTypes.FETCH_ACCOMMODATION_LOCATION_GEOLOCATIONS_REQUEST,
        googleMapsService.sortDistances,
        {
            origin: {
                location: {
                    latLng: {
                        latitude: referencePosition.latitude,
                        longitude: referencePosition.longitude,
                    },
                },
            },
            destinations: googlePlaceDTOs.map(googlePlaceDTO => ({
                apiUnrelatedFields: googlePlaceDTO,
                location: {
                    latLng: {
                        latitude: googlePlaceDTO.latitude,
                        longitude: googlePlaceDTO.longitude,
                    },
                },
            })),
        },
    );

    const fetchGeolocationsResponseAction = yield take([
        saaActionTypes.FETCH_ACCOMMODATION_LOCATION_GEOLOCATIONS_REQUEST_SUCCEEDED,
        saaActionTypes.FETCH_ACCOMMODATION_LOCATION_GEOLOCATIONS_REQUEST_FAILED,
    ]);

    if (fetchGeolocationsResponseAction.error) {
        yield* serviceAssignmentHelpers.setPersistenceReady(serviceAssignmentId);
        return;
    }

    const {response: fetchGeolocationsResponse} = fetchGeolocationsResponseAction.payload;
    const {distances} = fetchGeolocationsResponse;

    const accommodationLocationRecommendationDTOs = distances.map(distance => ({
        ...distance.destination.apiUnrelatedFields,
        routeToReferentialPoint: {
            totalKilometers: distance.distanceMeters / 1000,
            totalTravelTime: Number(distance.duration.replace('s', '')) / 60,
        },
    }));

    const recommendedAccommodationLocationDTO = accommodationLocationRecommendationDTOs[0];
    let updatedAccommodationLocationId = null;

    // Determine if selected SP is within suggestion results
    let selectedLocationFromSuggestions = null;
    if (acePartnerLocation) {
        const {locationName, id: locationId} = acePartnerLocation;

        selectedLocationFromSuggestions = accommodationLocationRecommendationDTOs
            .find(accommodationLocationDTO => {
                return accommodationLocationDTO['locationName'] === locationName
                    && accommodationLocationDTO['locationId'] === locationId;
            });

        if (!selectedLocationFromSuggestions) {
            accommodationLocationRecommendationDTOs.unshift({
                ...acePartnerLocation,
                locationId: acePartnerLocation.id,
                locationName: serviceAssignment?.acePartner?.name || locationName,
                phoneNo: serviceAssignment.acePartner.businessContactDetails?.phoneNo || acePartnerLocation.phoneNo,
            });
            updatedAccommodationLocationId = locationId;
        }

        // patch new version of ace partner's ID
        if (selectedLocationFromSuggestions && selectedLocationFromSuggestions.locationId !== locationId) {
            yield* updateServiceAssignment({
                caller: saaActionTypes.SUBMIT_SAA_ACCOMMODATION_LOCATION_FORM,
                assignmentType: efServiceAssignmentTypes.ACCOMMODATION,
                serviceAssignmentLineNo,
                serviceCaseId,
                serviceAssignmentData: {
                    acePartner: {
                        ...serviceAssignment.acePartner,
                        id: null, // see https://computerrock.atlassian.net/browse/ACELEA-575
                        externalId: selectedLocationFromSuggestions.locationId,
                        location: {
                            ...acePartnerLocation,
                            locationId: selectedLocationFromSuggestions.locationId,
                        },

                    },
                },
            });
        }
    }

    // save recommended accommodation location as selected if no accommodation location was previously set
    if ((!serviceAssignment.acePartner || !serviceAssignment.acePartner.location)
        && recommendedAccommodationLocationDTO) {
        const recommendedAccommodationLocation = new Location().fromDTO(recommendedAccommodationLocationDTO);
        const businessContactDetails = new BusinessContactDetails({
            phoneNo: recommendedAccommodationLocation.phoneNo || '',
        });

        yield* updateServiceAssignment({
            caller: 'LOAD_SAA_ACCOMMODATION_LOCATIONS',
            assignmentType: efServiceAssignmentTypes.ACCOMMODATION,
            serviceAssignmentLineNo,
            serviceCaseId,
            serviceAssignmentData: {
                acePartner: {
                    id: null, // see https://computerrock.atlassian.net/browse/ACELEA-575
                    externalId: recommendedAccommodationLocation.id,
                    location: {
                        ...recommendedAccommodationLocation,
                        locationId: recommendedAccommodationLocation.id,
                    },
                    name: recommendedAccommodationLocation.locationName || '',
                    businessContactDetails,
                    contactDetails: null,
                },
            },
        });

        // set persistence state back to PENDING
        yield* serviceAssignmentHelpers.setPersistencePending(serviceAssignmentId);
    }

    // store accommodation location options
    yield put({
        type: saaActionTypes.SET_SAA_ACCOMMODATION_LOCATION_RECOMMENDATIONS,
        payload: {
            serviceAssignmentId,
            accommodationLocationRecommendationDTOs,
            recommendedAccommodationLocationId: recommendedAccommodationLocationDTO
                ? recommendedAccommodationLocationDTO['locationId'] : null,
            updatedAccommodationLocationId: updatedAccommodationLocationId,
        },
    });

    yield* serviceAssignmentHelpers.setPersistenceReady(serviceAssignmentId);
};

export default searchSAAAccommodationLocationsByGoogle;
